import { ethers, BigNumber } from 'ethers';
import { ChainId, GasSetting, PUBLIC_RPCS } from '../config/networks';
import { calculateGasSettings } from '../../services/dexes/uniswap/execution/gasSettings';
/**
 * Executes a series of direct transactions using the provided wallet and gas settings.
 *
 * @param {IExecuteQuote} params - The parameters for executing the transactions.
 * @param {Array<ITransaction>} params.transactions - The list of transactions to execute.
 * @param {Wallet | PKPEthersWallet} params.wallet - The wallet to use for signing and sending the transactions.
 * @param {ChainId} params.network - The network on which the transactions will be executed.
 * @param {GasSetting} [params.gasSetting=GasSetting.MARKET] - The gas setting to use (default is Market).
 * @returns {Promise<Array<ethers.providers.TransactionReceipt>>} - A promise that resolves to an array of transaction receipts.
 *
 * @throws {Error} - Throws an error if there is an issue calculating gas settings or sending the transactions.
 */
export async function executeDirectTransactionsEVM({ transactions, wallet, network, gasSetting = GasSetting.MARKET, blockConfirmations, legacyPayloads, }) {
    // const RPC_URL =
    //   network == ChainId.ETHEREUM ? 'https://rpc.flashbots.net/fast' : PUBLIC_RPCS[network];
    const RPC_URL = PUBLIC_RPCS[network];
    const provider = new ethers.providers.JsonRpcProvider(RPC_URL);
    console.log('Executing order legacyPayloads', legacyPayloads);
    // Get the current nonce
    const nonce = await provider.getTransactionCount(wallet.address, 'pending');
    // Calculate gas settings
    let gasSettings;
    let isEIP1559;
    if (!(legacyPayloads === null || legacyPayloads === void 0 ? void 0 : legacyPayloads.length)) {
        try {
            gasSettings = await calculateGasSettings({
                network,
                transactions,
                gasSetting: network === ChainId.ETHEREUM ? GasSetting.MARKET : GasSetting.MARKET,
            });
            isEIP1559 =
                gasSettings.maxFeePerGas !== undefined && gasSettings.maxPriorityFeePerGas !== undefined;
        }
        catch (error) {
            console.log('Executing order Error calculating gas settings:', error);
            throw error;
        }
    }
    // Prepare and send transactions
    const txPromises = transactions.map(async (tx, i) => {
        var _a, _b;
        try {
            let txRequest = {
                to: tx.to,
                value: tx.value ? BigNumber.from(tx.value) : undefined,
                data: tx.data,
                nonce: nonce + i,
                chainId: provider.network.chainId, // Explicitly set chainId
            };
            if (legacyPayloads === null || legacyPayloads === void 0 ? void 0 : legacyPayloads.length) {
                console.log('Executing order maxFeePerGas', legacyPayloads[i]);
                let gasPrice;
                let gasLimit;
                if (!((_a = legacyPayloads === null || legacyPayloads === void 0 ? void 0 : legacyPayloads[i]) === null || _a === void 0 ? void 0 : _a.gasPrice)) {
                    gasPrice = BigNumber.from(Math.floor(parseFloat((await provider.getGasPrice()).toString()) * 1.5));
                    gasLimit = BigInt(Math.floor((parseFloat(legacyPayloads[i].gasLimit)) * 1.5));
                }
                else {
                    gasPrice = BigNumber.from(legacyPayloads[i].gasPrice);
                    gasLimit = BigNumber.from(legacyPayloads[i].gasLimit);
                }
                txRequest = Object.assign(Object.assign({}, txRequest), { gasPrice: gasPrice, gasLimit: gasLimit, type: 1 });
                console.log('Executing order gasPrice legacyyyy', legacyPayloads[i], txRequest);
            }
            else if (isEIP1559) {
                txRequest = Object.assign(Object.assign({}, txRequest), { maxFeePerGas: BigNumber.from(gasSettings.maxFeePerGas), maxPriorityFeePerGas: BigNumber.from(gasSettings.maxPriorityFeePerGas), type: 2 });
            }
            else {
                txRequest = Object.assign(Object.assign({}, txRequest), { gasPrice: BigNumber.from(gasSettings.gasPrice), type: 1 });
            }
            // Set gas limit
            if (!(legacyPayloads === null || legacyPayloads === void 0 ? void 0 : legacyPayloads.length)) {
                txRequest.gasLimit = BigNumber.from(gasSettings.gasEstimations[i]);
            }
            console.log('Executing order txRequest', txRequest);
            // Sign the transaction
            const signedTx = await wallet.signTransaction(txRequest);
            // Send the raw transaction
            let result;
            // Send the raw transaction
            if (network === ChainId.ETHEREUM) {
                const otherRpcs = [
                    //public rpc
                    'https://eth.llamarpc.com',
                    //flashbots
                    'https://rpc.flashbots.net/fast',
                    // flashbots via quicknode
                    'https://warmhearted-wandering-film.quiknode.pro/4af011d6b375356e326c0a93810b466e30c2c9c5/',
                    // blink via quicknode
                    'https://lingering-broken-firefly.quiknode.pro/cb8d5b866a03cbf62902c60dc4d7194e65aeacb5/'
                ];
                const otherProviders = otherRpcs.map((rpc) => new ethers.providers.JsonRpcProvider(rpc));
                const allProviders = [provider, ...otherProviders];
                const results = (await Promise.all(allProviders.map(async (provider) => {
                    try {
                        return await provider.sendTransaction(signedTx);
                    }
                    catch (e) {
                        return 'Error: ' + e.message;
                    }
                })));
                //@ts-ignore
                const errors = results === null || results === void 0 ? void 0 : results.filter((x) => (x === null || x === void 0 ? void 0 : x.hash) === undefined);
                //@ts-ignore
                result = (_b = results === null || results === void 0 ? void 0 : results.filter((x) => x === null || x === void 0 ? void 0 : x.hash)) === null || _b === void 0 ? void 0 : _b[0];
                console.log('executing order result ethereum', results);
                if (!result) {
                    throw new Error('Error sending transaction ethereum' + JSON.stringify(errors, null, 2));
                }
            }
            else {
                result = await provider.sendTransaction(signedTx);
                console.log('Executing order result', result);
            }
            return result;
        }
        catch (error) {
            console.error('Executing order result Error sending transaction:', error);
            throw error;
        }
    });
    try {
        const txResponses = await Promise.all(txPromises);
        // Wait for all transactions to be mined
        console.log('Executing order txResponses', txResponses, blockConfirmations);
        const txResponsesWithHash = txResponses.map((tx, i) => {
            return Object.assign({ transactionHash: tx.hash }, tx);
        });
        console.log('Executing order txResponsesWithHash', txResponsesWithHash, blockConfirmations);
        let receiptPromises;
        if (blockConfirmations == 0) {
            //@ts-ignore
            return txResponsesWithHash;
        }
        if (blockConfirmations) {
            receiptPromises = txResponses.map((tx) => tx.wait(blockConfirmations));
        }
        else {
            receiptPromises = txResponses.map((tx) => tx.wait());
        }
        return await Promise.all(receiptPromises);
    }
    catch (error) {
        console.error('Error sending transactions:', error);
        throw error;
    }
}
